import type { WorkbookFeedStatus } from '@erp/rpc';

/**
 * A Report's feeds as a state-store patch: each feed's value at `/<key>`, which
 * is what the view's `{ "$state": "/<key>" }` reads, and its status at
 * `/$data/<key>`. A feed that failed keeps the value the view already has.
 */
export function feedsPatch(
  feeds: Readonly<Record<string, WorkbookFeedStatus>>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, feed] of Object.entries(feeds)) {
    if (feed.status === 'ready') {
      patch[`/${key}`] = feed.value;
      patch[`/$data/${key}`] = { loading: false, error: null };
    } else {
      patch[`/$data/${key}`] = { loading: false, error: feed.message };
    }
  }
  return patch;
}

/** Every feed marked loading while a read with new inputs is in flight. */
export function loadingPatch(
  feeds: Readonly<Record<string, WorkbookFeedStatus>>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(feeds).map(([key, feed]) => [
      `/$data/${key}`,
      {
        loading: true,
        error: feed.status === 'error' ? feed.message : null,
      },
    ]),
  );
}

/**
 * Every feed out of loading when a read failed as a whole: each keeps the
 * value the view already has and shows why it wasn't refreshed.
 */
export function failedReadPatch(
  feeds: Readonly<Record<string, WorkbookFeedStatus>>,
  message: string,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.keys(feeds).map((key) => [
      `/$data/${key}`,
      { loading: false, error: message },
    ]),
  );
}

/** The values the feeds read, by pointer, from wherever the view holds them. */
export function readInputs(
  get: (pointer: string) => unknown,
  pointers: readonly string[],
): Record<string, unknown> {
  return Object.fromEntries(
    pointers.flatMap((pointer) => {
      const value = get(pointer);
      return value === undefined ? [] : [[pointer, value]];
    }),
  );
}

/**
 * Saved inputs (from the URL or this browser) as a state-store patch, keeping
 * only the pointers this version of the Report reads.
 */
export function inputsPatch(
  saved: unknown,
  pointers: readonly string[],
): Record<string, unknown> {
  if (typeof saved !== 'object' || saved === null || Array.isArray(saved))
    return {};
  return Object.fromEntries(
    pointers.flatMap((pointer) =>
      Object.hasOwn(saved, pointer)
        ? [[pointer, (saved as Record<string, unknown>)[pointer]]]
        : [],
    ),
  );
}

function savedInputsKey(appId: string): string {
  return `report-inputs:${appId}`;
}

/** This browser's last inputs for a Report; empty when storage is unavailable. */
export function loadSavedInputs(appId: string): unknown {
  try {
    const raw = window.localStorage.getItem(savedInputsKey(appId));
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

export function saveInputs(
  appId: string,
  inputs: Record<string, unknown>,
): void {
  try {
    window.localStorage.setItem(savedInputsKey(appId), JSON.stringify(inputs));
  } catch {
    // Remembering filters is a convenience; the URL still carries them.
  }
}

/** Filter inputs as an object; anything else is no inputs. */
export function inputsOrEmpty(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
